import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Accessibility, ArrowRight, ExternalLink, FileText, Map, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollReveal } from "@/components/motion/ScrollReveal";
import { registrationEnabled } from "@/config/features";
import { TERMS_URL } from "@/config/documents";
import { FEES } from "@/components/registration/ParticipationFeesDialog";
import { formatCurrencyMYR } from "@/lib/format";
import {
  accessibleTransit,
  meetUpDetails,
  programme,
  venueMapEmbedUrl,
  villageNotes,
  type ProgrammeItem,
  type ProgrammePhase
} from "./data";

/*
 * Accent tones for the programme timeline. All phases use the race red so
 * the times read consistently down the list (client request: match every
 * item to the race items).
 *   race   → race red (the headline moment — flag-off)
 *   dinner → race red (match the race items)
 *   end    → race red (match the race items)
 */
const PHASE_TONE_TEXT: Record<ProgrammePhase, string> = {
  race: "text-primary",
  dinner: "text-primary",
  end: "text-primary"
};

const PHASE_TONE_DOT_BG: Record<ProgrammePhase, string> = {
  race: "bg-primary",
  dinner: "bg-primary",
  end: "bg-primary"
};

// Race village / route map, delivered as a static asset in `public/`.
const RACE_ROUTE_SRC = "/race-route.png";

/**
 * Variant A — Section-led (current default).
 * Distinct sections grouped by topic. Best for skimmers — runners can
 * jump straight to the part they need (transit / map / programme).
 */
export function RaceDaySectionLed() {
  return (
    <>
      <TeamParticipation />
      <TeamCategories />
      <MeetUp />
      <RaceRoute />
      <Programme />
      <RaceVillageMap />
      <GettingThere />
    </>
  );
}

/**
 * Team Participation Commitment table — the sliding-scale contribution per
 * company (1–10 teams). Surfaced on the public Race Day page so companies can
 * see the fee tiers before signing in. Reuses the `FEES` data + `dashboard`
 * i18n strings that back the in-dashboard ParticipationFeesDialog, and the
 * on-page table styling from the sponsorship PrizeCategoriesTable.
 */
function TeamParticipation() {
  const { t, i18n } = useTranslation(["raceday", "dashboard"]);

  return (
    <section className="container py-16 md:py-20">
      {/* Two-column row from md: heading text left, Terms & Conditions pill
          right. The description is capped at max-w-2xl, so on a wide container
          the right-hand space is otherwise empty — this is where the client
          asked for the T&C. `items-end` lands the pill's bottom edge level with
          the description, forming a shelf directly above the fees table. */}
      <ScrollReveal className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">{t("teamParticipation.eyebrow")}</p>
          <h2 className="mt-2 text-4xl md:text-5xl">{t("teamParticipation.heading")}</h2>
          <p className="mt-3 max-w-2xl text-muted-foreground">{t("teamParticipation.body")}</p>
        </div>

        {/* A single-line pill rather than a panel: it fills the circled space
            without competing with the 48px heading beside it, and stays
            content-width when it stacks under the description on a phone.
            Same lift/focus treatment as the race-route link. */}
        <a
          href={TERMS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-[44px] shrink-0 items-center gap-2.5 self-start rounded-full border bg-card px-4 py-2.5 text-sm font-semibold transition-transform hover:-translate-y-0.5 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 md:self-auto"
        >
          <FileText className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          {t("teamParticipation.termsTitle")}
          <span className="font-normal text-muted-foreground">{t("teamParticipation.termsFormat")}</span>
          <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          {/* The external-link icon is the sighted affordance; screen readers
              get the warning here. WCAG G201 for the unannounced target change. */}
          <span className="sr-only"> ({t("teamParticipation.termsNewTab")})</span>
        </a>
      </ScrollReveal>

      <ScrollReveal className="mt-6" delay={0.05}>
        <div className="overflow-x-auto rounded-xl border bg-card">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-muted/40">
                <th className="text-left p-4 font-medium text-muted-foreground border-b">
                  <span className="text-xs uppercase tracking-[0.2em]">{t("dashboard:fees.teamsHeader")}</span>
                </th>
                <th className="text-right p-4 font-medium text-muted-foreground border-b border-l">
                  <span className="text-xs uppercase tracking-[0.2em]">{t("dashboard:fees.contributionHeader")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {FEES.map((row, idx) => {
                const teamLabel =
                  row.teams === 1
                    ? t("dashboard:fees.oneTeam")
                    : t("dashboard:fees.manyTeams", { word: t(`dashboard:fees.teamWord.${row.teams}`) });
                return (
                  <tr key={row.teams} className={idx % 2 === 0 ? "bg-background" : "bg-muted/20"}>
                    <td className="p-4">
                      {teamLabel} <span className="text-muted-foreground">({row.teams})</span>
                    </td>
                    <td className="p-4 border-l text-right font-display text-base tracking-normal">
                      {formatCurrencyMYR(row.amount, i18n.language)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </ScrollReveal>
    </section>
  );
}

/**
 * Team Categories table — how a team is classified (Mixed / Senior) by its
 * runner line-up, laid out like the client's published table: a row-spanning
 * "Main Race - 5km" label, then a "CEO Race - 1.6km" row whose eligibility
 * copy spans both category columns. ("Open" was retired in Sep 2026 and
 * merged into Mixed.)
 * Reuses the `dashboard:categoryCriteria.*` strings and column logic from
 * CategoryCriteriaDialog; muted section band keeps the top-of-page
 * alternation (plain → muted → plain MeetUp).
 */
function TeamCategories() {
  const { t } = useTranslation(["raceday", "dashboard"]);

  const columns = [
    { key: "mixed", name: t("dashboard:categoryCriteria.mixed"), sub: "", combos: t("dashboard:categoryCriteria.mixedCombos", { returnObjects: true }) as string[] },
    { key: "senior", name: t("dashboard:categoryCriteria.senior"), sub: t("dashboard:categoryCriteria.seniorSub"), combos: t("dashboard:categoryCriteria.seniorCombos", { returnObjects: true }) as string[] }
  ];
  const rowCount = Math.max(...columns.map((c) => (Array.isArray(c.combos) ? c.combos.length : 0)));
  const ceoLines = t("dashboard:categoryCriteria.ceoRaceLines", { returnObjects: true }) as string[];

  return (
    <section className="bg-muted/30 border-y border-border">
      <div className="container py-16 md:py-20">
        <ScrollReveal>
          <p className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">{t("teamCategories.eyebrow")}</p>
          <h2 className="mt-2 text-4xl md:text-5xl">{t("teamCategories.heading")}</h2>
          <p className="mt-3 max-w-2xl text-muted-foreground">{t("teamCategories.body")}</p>
        </ScrollReveal>

        <ScrollReveal className="mt-6" delay={0.05}>
          <div className="overflow-x-auto rounded-xl border bg-card">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-muted/40">
                  <th aria-hidden className="p-4 border-b" />
                  {columns.map((c) => (
                    <th key={c.key} className="text-left align-top p-4 border-b border-l">
                      <span className="font-semibold text-foreground">{c.name}</span>
                      {c.sub ? (
                        <span className="mt-1 block text-xs font-normal text-muted-foreground">({c.sub})</span>
                      ) : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: rowCount }).map((_, rowIdx) => (
                  <tr key={rowIdx} className={rowIdx % 2 === 0 ? "bg-background" : "bg-muted/20"}>
                    {rowIdx === 0 ? (
                      <th
                        scope="row"
                        rowSpan={rowCount}
                        className="text-left align-middle p-4 border-b bg-muted/40 font-semibold text-foreground"
                      >
                        {t("dashboard:categoryCriteria.mainRace")}
                        <span className="mt-1 block text-xs font-normal text-muted-foreground">
                          {t("dashboard:categoryCriteria.mainRaceSub")}
                        </span>
                      </th>
                    ) : null}
                    {columns.map((c) => (
                      <td key={c.key} className="p-4 align-top text-muted-foreground border-b border-l">
                        {Array.isArray(c.combos) ? c.combos[rowIdx] ?? "" : ""}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr className="bg-background">
                  <th scope="row" className="text-left align-middle p-4 bg-muted/40 font-semibold text-foreground">
                    {t("dashboard:categoryCriteria.ceoRace")}
                  </th>
                  <td colSpan={columns.length} className="p-4 text-center text-muted-foreground border-l">
                    {Array.isArray(ceoLines)
                      ? ceoLines.map((line) => (
                          <span key={line} className="block">
                            {line}
                          </span>
                        ))
                      : null}
                    <span className="mt-3 block text-xs italic">{t("dashboard:categoryCriteria.ceoRaceNote")}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}

function MeetUp() {
  const { t } = useTranslation("raceday");

  return (
    <section className="container py-16 md:py-20">
      <ScrollReveal className="grid gap-10 lg:grid-cols-2">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">{t("meetUp.eyebrow")}</p>
          <h2 className="mt-2 text-4xl md:text-5xl">{t("meetUp.heading")}</h2>
          <p className="mt-4 text-muted-foreground">
            {t("meetUp.body")}
          </p>
          <div className="mt-6 space-y-3 text-sm">
            {meetUpDetails.map((d) => (
              <div key={d.key} className="flex items-start gap-3">
                <d.icon className="h-5 w-5 text-primary mt-0.5" />
                <p className="whitespace-pre-line sm:whitespace-normal">{t(`meetUp.${d.key}`)}</p>
              </div>
            ))}
            <div className="flex items-start gap-3">
              <svg aria-hidden viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 text-primary mt-0.5 shrink-0">
                <path d="M13.49 5.48c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm-3.6 13.9l1-4.4 2.1 2v6h2v-7.5l-2.1-2 .6-3c1.3 1.5 3.3 2.5 5.5 2.5v-2c-1.9 0-3.5-1-4.3-2.4l-1-1.6c-.4-.6-1-1-1.7-1-.3 0-.5.1-.8.1l-5.2 2.2v4.7h2v-3.4l1.8-.7-1.6 8.1-4.9-1-.4 2 7 1.4z" />
              </svg>
              <p>{t("meetUp.teams")}</p>
            </div>
          </div>
        </div>

        {/* Venue location map — Google Maps embed (src in ./data so the pin is data-driven). */}
        <div className="relative aspect-[4/3] overflow-hidden rounded-xl border border-border">
          <iframe
            src={venueMapEmbedUrl}
            title={t("meetUp.mapTitle")}
            className="absolute inset-0 h-full w-full border-0"
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </ScrollReveal>
    </section>
  );
}

function RaceRoute() {
  const { t } = useTranslation("raceday");

  return (
    <section className="container py-16 md:py-20">
      <ScrollReveal>
        <p className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">{t("raceRoute.eyebrow")}</p>
        <h2 className="mt-2 text-4xl md:text-5xl">{t("raceRoute.heading")}</h2>
        <p className="mt-3 max-w-2xl text-muted-foreground">{t("raceRoute.body")}</p>
      </ScrollReveal>

      <ScrollReveal className="mt-10" delay={0.05}>
        {/* Full route map — click to open the full-resolution image in a new tab. */}
        <a
          href={RACE_ROUTE_SRC}
          target="_blank"
          rel="noopener noreferrer"
          className="group block overflow-hidden rounded-xl border border-border bg-card shadow-xl transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          <img
            src={RACE_ROUTE_SRC}
            loading="lazy"
            className="block h-auto w-full"
            alt={t("raceRoute.heading")}
          />
        </a>
      </ScrollReveal>
    </section>
  );
}

function Programme() {
  const { t } = useTranslation("raceday");

  return (
    <section className="bg-muted/30 border-y border-border">
      <div className="container py-16 md:py-20">
        <ScrollReveal>
          <p className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">{t("programme.eyebrow")}</p>
          <h2 className="mt-2 text-4xl md:text-5xl">{t("programme.heading")}</h2>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            {t("programme.body")}
          </p>
        </ScrollReveal>

        <div className="mt-10 space-y-12">
          <div className="space-y-3">
            {programme.map((item) => (
              <ProgrammeRow key={`${item.time}-${item.key}`} item={item} />
            ))}
          </div>

          <PrizePresentationBlock />
        </div>
      </div>
    </section>
  );
}

function ProgrammeRow({ item }: { item: ProgrammeItem }) {
  const { t } = useTranslation("raceday");

  /*
   * Time stamp now picks up its phase's celebration tone, and a small
   * accent dot anchors each row to its phase visually.
   */
  const toneText = PHASE_TONE_TEXT[item.phase];
  const toneDot = PHASE_TONE_DOT_BG[item.phase];

  const bullets = item.hasBullets
    ? (t(`programme.items.${item.key}.bullets`, { returnObjects: true }) as string[])
    : null;
  // Notes are optional per item; defaultValue "" lets us treat "absent" as falsy.
  const note = t(`programme.items.${item.key}.note`, { defaultValue: "" });

  return (
    <ScrollReveal>
      <div className="grid grid-cols-[auto_1fr] gap-6 rounded-lg border bg-card p-5 items-start">
        <div className="text-right min-w-[88px]">
          <p className={`font-display text-2xl tracking-normal whitespace-nowrap ${toneText}`}>
            {item.time}
          </p>
        </div>
        <div>
          {bullets ? (
            <div className="space-y-1">
              {bullets.map((b, i) => (
                <p key={i} className="flex items-center gap-2 font-semibold">
                  <span aria-hidden className={`h-1.5 w-1.5 rounded-full ${toneDot}`} />
                  {b}
                </p>
              ))}
            </div>
          ) : (
            <p className="flex items-center gap-2 font-semibold">
              <span aria-hidden className={`h-1.5 w-1.5 rounded-full ${toneDot}`} />
              {t(`programme.items.${item.key}.title`)}
            </p>
          )}
          {note && <p className="text-sm text-muted-foreground mt-1">{note}</p>}
        </div>
      </div>
    </ScrollReveal>
  );
}

function PrizePresentationBlock() {
  const { t } = useTranslation("raceday");
  const categories = t("prize.categories", { returnObjects: true }) as string[];

  /*
   * Trophy + prize categories now ride on the sponsor gold (`accent`)
   * token, keeping prize-presentation distinct from the dinner phase
   * (magenta) without losing the "medal" feel.
   */
  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="rounded-lg bg-accent/15 p-2 text-accent-foreground ring-1 ring-accent/40">
          <Trophy className="h-5 w-5 text-accent-strong" />
        </div>
        <p className="font-display text-xl tracking-normal text-foreground/80">
          {t("prize.heading")}
        </p>
      </div>
      <div className="rounded-lg border bg-card p-6">
        <p className="text-sm text-muted-foreground mb-4">
          {t("prize.intro")}
        </p>
        <ul className="grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
          {categories.map((category) => (
            <li key={category} className="flex gap-2">
              <span aria-hidden className="mt-2 h-1 w-1 rounded-full bg-accent shrink-0" />
              <span>{category}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function RaceVillageMap() {
  const { t } = useTranslation("raceday");

  return (
    <section className="container py-16 md:py-20">
      <ScrollReveal>
        <p className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">{t("raceVillage.eyebrow")}</p>
        <h2 className="mt-2 text-4xl md:text-5xl">{t("raceVillage.heading")}</h2>
      </ScrollReveal>

      <ScrollReveal className="mt-10 grid gap-6 lg:grid-cols-3" delay={0.05}>
        <div className="lg:col-span-2 aspect-[16/9] lg:aspect-auto rounded-xl border-2 border-dashed border-border bg-muted/40 grid place-items-center">
          <div className="text-center px-6">
            <Map className="h-10 w-10 text-foreground/40 mx-auto" />
            <p className="mt-3 font-display text-xl tracking-normal text-foreground/60">{t("raceVillage.mapTitle")}</p>
            <p className="text-sm text-muted-foreground">
              {t("raceVillage.mapBody")}
            </p>
          </div>
        </div>

        <div className="space-y-4">
          {villageNotes.map((n) => {
            /*
             * Promote the PWD card with a teal left-rim so the
             * inclusivity message (the poster's "Run For All" promise)
             * earns visual weight. Other cards keep the neutral border.
             */
            const isPwd = n.icon === Accessibility;
            return (
              <Card
                key={n.key}
                className={isPwd ? "border-l-4 border-l-accent-teal" : undefined}
              >
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <n.icon
                      className={`h-5 w-5 ${isPwd ? "text-accent-teal" : "text-primary"}`}
                    />
                    {t(`raceVillage.notes.${n.key}.title`)}
                    {isPwd && (
                      <span className="ml-auto inline-flex items-center rounded-full bg-accent-teal/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-accent-teal">
                        {t("raceVillage.runForAll")}
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground">{t(`raceVillage.notes.${n.key}.body`)}</CardContent>
              </Card>
            );
          })}
        </div>
      </ScrollReveal>
    </section>
  );
}

function GettingThere() {
  const { t } = useTranslation("raceday");

  return (
    <section className="bg-muted/30 border-y border-border">
      <div className="container py-16 md:py-20">
        <ScrollReveal>
          <p className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">{t("gettingThere.eyebrow")}</p>
          <h2 className="mt-2 text-4xl md:text-5xl">{t("gettingThere.heading")}</h2>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            {t("gettingThere.body")}
          </p>
        </ScrollReveal>

        <ScrollReveal.Group className="mt-10 grid gap-6 md:grid-cols-2" stagger={0.08}>
          {/* Titled card per line with a bulleted station list (client-requested
              revert to the pre-b69a167 style). Station names live in i18n as an
              array — same returnObjects pattern as the prize categories list. */}
          {accessibleTransit.map((line) => {
            const stations = t(`gettingThere.transit.${line.key}.stations`, {
              returnObjects: true
            }) as string[];
            return (
              <ScrollReveal.Item key={line.key}>
                <div className="rounded-xl border bg-card p-6">
                  <div className="flex items-center gap-3">
                    <div className="rounded-lg bg-primary/10 p-2 text-primary shrink-0">
                      <line.icon className="h-5 w-5" />
                    </div>
                    <h3 className="font-display text-2xl tracking-wide">
                      {t(`gettingThere.transit.${line.key}.title`)}
                    </h3>
                  </div>
                  <ul className="mt-4 space-y-3 text-sm">
                    {stations.map((station) => (
                      <li key={station} className="flex gap-3">
                        <span aria-hidden className="mt-2 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                        <p className="font-medium">{station}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              </ScrollReveal.Item>
            );
          })}
        </ScrollReveal.Group>

        {registrationEnabled && (
          <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              {t("gettingThere.ctaText")}
            </p>
            <Link to="/registration">
              <Button size="xl" variant="glow">
                {t("gettingThere.ctaButton")} <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
